import { z } from 'zod';
import { userRoleSchema, userSchema } from './user.js';
/**
 * Which of the two front-ends a request is authenticating against.
 *
 * `portal` is the candidate/interviewer web app; `backoffice` is the admin-only backoffice.
 * The API rejects a role that does not belong to the stated audience, so the separation is
 * enforced server-side rather than by the backoffice's post-login client check alone — a
 * check the API had already defeated by minting a fully-privileged token before it ran.
 */
export const loginAudienceSchema = z.enum(['portal', 'backoffice']);
/**
 * Roles each audience admits.
 *
 * Admins appear in both: the web app's interviewer routes accept them (see
 * `apps/web/src/App.tsx`, which guards on `['interviewer', 'admin']`) and the API's
 * interviewer endpoints do the same, so refusing an admin at the portal would break a
 * capability they already have. Interviewers are portal-only — the backoffice is
 * administrators only, and its own guard says so.
 */
export const ROLES_BY_LOGIN_AUDIENCE = {
    portal: ['candidate', 'interviewer', 'admin'],
    backoffice: ['admin'],
};
export function loginAudienceAdmits(audience, role) {
    return ROLES_BY_LOGIN_AUDIENCE[audience].includes(role);
}
export const loginRequestSchema = z.object({
    email: z.email(),
    password: z.string().min(1),
    /**
     * Optional on the wire so an older client (a desktop build shipped before this field
     * existed) keeps working; absent means `portal`, which is the audience every such client
     * was in fact using. The backoffice must send it explicitly — the restrictive value is
     * never the one you get by omission, so a forgotten field cannot silently widen access.
     */
    audience: loginAudienceSchema.default('portal'),
});
export const loginResponseSchema = z.object({
    accessToken: z.string().min(1),
    expiresAt: z.iso.datetime(),
    user: userSchema,
});
export const PASSWORD_MIN_LENGTH = 12;
/** Consumes a single-use signed invite token and sets the account's first password. */
export const passwordSetupRequestSchema = z.object({
    inviteToken: z.string().min(1),
    password: z.string().min(PASSWORD_MIN_LENGTH),
});
/**
 * Starts a password recovery. The response is deliberately uninformative — see
 * `forgotPasswordResponseSchema`.
 */
export const forgotPasswordRequestSchema = z.object({
    email: z.email(),
});
/**
 * The same body whether or not the address has an account. Nothing in it is derived from
 * the lookup, so it cannot be used to enumerate registered addresses.
 */
export const forgotPasswordResponseSchema = z.object({
    status: z.literal('accepted'),
});
/**
 * Consumes a single-use password-reset token and replaces the account's password. Mirrors
 * `passwordSetupRequestSchema` so both flows share the client's form handling, and answers
 * with a `LoginResponse` so a completed reset signs the user straight in.
 */
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(PASSWORD_MIN_LENGTH),
});
export const jwtClaimsSchema = z.object({
    sub: z.uuid(),
    role: userRoleSchema,
    email: z.email(),
    /** Seconds since epoch, per RFC 7519 — not ISO strings like the rest of the API. */
    iat: z.number().int(),
    exp: z.number().int(),
    iss: z.string().min(1),
});
//# sourceMappingURL=auth.js.map